import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { theme } from "@polifonia/theme";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import { interlude } from "@/interlude";
import { RevealOnceKey } from "@/pages/applications/pages/apiKeys/components/RevealOnceKey";
import {
  API_KEY_NAME_MAX_LENGTH,
  validateApiKeyName,
} from "@/pages/applications/pages/apiKeys/utils/apiKeyName";
import { isHandledGlobally } from "@/pages/applications/pages/apiKeys/utils/isHandledGlobally";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";

interface FormValues {
  name: string;
}

interface CreateApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> =
  function CreateApiKeyModal({ open, onClose, onSuccess }) {
    const { t } = useTranslation();
    const [apiError, setApiError] = useState<string | null>(null);
    /**
     * The issued key, held for exactly as long as this modal is open.
     *
     * Holding it here is what makes the reveal happen once: the list endpoint
     * masks every key it renders, so when this state goes the full value is
     * gone from the app for good.
     */
    const [issuedKey, setIssuedKey] = useState<string | null>(null);

    const { control, handleSubmit, reset, watch } = useForm<FormValues>({
      mode: "onChange",
      defaultValues: { name: "" },
    });

    const watchedName = watch("name");

    const nameErrorMessages = useMemo(
      () => ({
        required: t("apiKey.error.nameRequired", "Enter a name for this key."),
        tooLong: t("apiKey.error.nameTooLong", "Use at most 128 characters."),
        forbiddenCharacter: t(
          "apiKey.error.nameForbiddenCharacter",
          "Spaces, commas, slashes and control characters are not allowed.",
        ),
      }),
      [t],
    );

    const createMutation = interlude.apiKey.useCreateApiKey(undefined, {
      onSuccess: (created) => {
        setIssuedKey(created.key);
        onSuccess();
      },
      onError: (error) => {
        /**
         * Only for what the global handler stays silent about. The four
         * `OPENAPI_*` codes now raise a snackbar of their own, so repeating
         * them here would show the same failure twice; what is left is the 422
         * interlude's name validators produce, which carries no code and
         * belongs next to the field that caused it.
         */
        if (isHandledGlobally(error)) return;
        setApiError(
          t(
            "apiKey.error.createFailed",
            "The key could not be created. Check the name and try again.",
          ),
        );
      },
    });

    const handleClose = useHandler(() => {
      reset({ name: "" });
      setApiError(null);
      setIssuedKey(null);
      createMutation.reset();
      onClose();
    });

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      },
    );

    const onSubmit = useHandler((values: FormValues) => {
      setApiError(null);
      /**
       * Sent exactly as typed. The rules are mirrored from interlude rather
       * than applied to the value: trimming or stripping here would store a
       * name the reader never chose, and they have to recognize this key in
       * the list later.
       */
      createMutation.mutate({ name: values.name });
    });

    const footer: React.ReactNode = useMemo(() => {
      if (issuedKey !== null) {
        return (
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button variant="primary" onClick={handleClose}>
              {t("apiKey.createModal.done", "Done")}
            </Button>
          </Stack>
        );
      }

      return (
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="plain" onClick={handleClose}>
            {t("apiKey.createModal.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={
              validateApiKeyName(watchedName) !== null ||
              createMutation.isPending
            }
            loading={createMutation.isPending}
          >
            {t("apiKey.createModal.submit", "Create")}
          </Button>
        </Stack>
      );
    }, [
      issuedKey,
      handleClose,
      handleSubmit,
      onSubmit,
      watchedName,
      createMutation.isPending,
      t,
    ]);

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={
          issuedKey !== null
            ? t("apiKey.createModal.issued.title", "API key created")
            : t("apiKey.createModal.title", "Create API key")
        }
        width={520}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          {issuedKey !== null ? (
            <RevealOnceKey apiKeyValue={issuedKey} />
          ) : (
            <>
              <Typography variant="body" color={theme.colors.staticFgNote}>
                {t(
                  "apiKey.createModal.description",
                  "Name the key after the system that will use it, so it can be recognized in this list later.",
                )}
              </Typography>

              {apiError ? (
                <InfoBox
                  variant="error"
                  title={t(
                    "apiKey.error.createFailed.title",
                    "Could not create the key",
                  )}
                  description={apiError}
                />
              ) : null}

              <Box sx={sharedStyles.formSection}>
                <Box sx={sharedStyles.label}>
                  <Typography
                    variant="body"
                    fontWeight={500}
                    color={theme.colors.staticFgBody}
                  >
                    {t("apiKey.createModal.name.label", "Name")}
                  </Typography>
                  <Box component="span" sx={sharedStyles.required}>
                    *
                  </Box>
                </Box>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const violation = validateApiKeyName(value);
                      if (!violation) return true;
                      return nameErrorMessages[violation];
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <TextField
                        {...field}
                        fullWidth
                        placeholder={t(
                          "apiKey.createModal.name.placeholder",
                          "order-sync",
                        )}
                        error={!!error}
                        disabled={createMutation.isPending}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <Box component="span" sx={sharedStyles.charCount}>
                                <Typography variant="note" color="inherit">
                                  {watchedName.length}/{API_KEY_NAME_MAX_LENGTH}
                                </Typography>
                              </Box>
                            ),
                          },
                        }}
                      />
                      {/*
                       * No `maxLength` on the input, and no auto-correction of
                       * what was typed. A pasted name over the limit has to
                       * say so rather than be silently cut, which is the same
                       * reason the forbidden characters are explained instead
                       * of removed.
                       */}
                      {error ? (
                        <FormHelperText error>{error.message}</FormHelperText>
                      ) : (
                        <FormHelperText>
                          {t(
                            "apiKey.createModal.name.hint",
                            "Up to 128 characters. Spaces, commas and slashes are not allowed.",
                          )}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </Box>
            </>
          )}
        </Stack>
      </Modal>
    );
  };
